import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useSubscriptionCheck } from "./useSubscriptionCheck";
import { useTeamLead } from "./useTeamLead";
import { isDevEnvironment } from "env/runtimeEnv";

interface UseUserAndLeadSubscriptionCheckOptions {
  userId: string | null;
}

/**
 * Custom hook to check both user and team lead subscriptions
 * Handles navigation to subscription-details page if needed
 * Returns loading states and subscription-details validity
 */
export function useUserAndLeadSubscriptionCheck({
  userId,
}: UseUserAndLeadSubscriptionCheckOptions) {
  const navigate = useNavigate();

  // Get team lead UID
  const { leadUid, isLoadingLeadUid } = useTeamLead({ userId: userId });

  // Check user's own subscription-details
  const {
    isCheckingSubscription: isCheckingUserSubscription,
    isSubscriptionValid: isUserSubscriptionValid,
  } = useSubscriptionCheck({
    userId,
  });

  // Check team lead's subscription-details if leadUid exists
  const {
    isCheckingSubscription: isCheckingLeadSubscription,
    isSubscriptionValid: isLeadSubscriptionValid,
  } = useSubscriptionCheck({
    userId: leadUid || null,
  });

  // Check if user has reviewed subscriptions
  const [reviewedSubscriptions, setReviewedSubscriptions] = useState<
    boolean | null
  >(null);
  const [isLoadingReviewedSubscriptions, setIsLoadingReviewedSubscriptions] =
    useState(true);

  // Check if user is a team lead
  const [isTeamLead, setIsTeamLead] = useState<boolean>(false);
  const [isLoadingIsTeamLead, setIsLoadingIsTeamLead] = useState(true);

  // Combined loading state
  const isCheckingSubscription =
    isCheckingUserSubscription || isCheckingLeadSubscription;
  const isLoading =
    isLoadingLeadUid ||
    isCheckingSubscription ||
    isLoadingReviewedSubscriptions ||
    isLoadingIsTeamLead;

  // Fetch reviewedSubscriptions and isLead from user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setIsLoadingReviewedSubscriptions(false);
        setIsLoadingIsTeamLead(false);
        return;
      }

      try {
        const { getAccount } = await import("../../../api/accountApi");
        const account = await getAccount();
        setIsTeamLead(account?.isLead === true);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setReviewedSubscriptions(false);
        setIsTeamLead(false);
      } finally {
        setIsLoadingReviewedSubscriptions(false);
        setIsLoadingIsTeamLead(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  // Navigate to subscription-details page if both subscriptions are invalid
  useEffect(() => {
    // Skip subscription-details checks for guest users
    /*
    if (!email) {
      console.log("👤 Guest mode: Skipping subscription-details checks");
      return;
    }

     */

    // Wait for reviewedSubscriptions and isTeamLead to be loaded
    if (isLoadingReviewedSubscriptions || isLoadingIsTeamLead) {
      return;
    }

    // Don't redirect if user has reviewed subscriptions

    /*
        if (reviewedSubscriptions === true) {
            console.log('✅ User has reviewed subscriptions - skipping redirect');
            return;
        }
            */

    // Wait for leadUid to be fetched and all subscription-details checks to complete
    if (isLoadingLeadUid || isCheckingSubscription) {
      return;
    }

    // If user subscription-details is valid, no need to navigate
    if (isUserSubscriptionValid === true) {
      return;
    }

    // Check if a purchase is in progress - if so, skip redirect to prevent loops
    const purchaseInProgress =
      localStorage.getItem("subscription_purchase_in_progress") === "true";
    if (purchaseInProgress) {
      console.log(
        "⏳ Purchase in progress - skipping subscription-details check redirect"
      );
      return;
    }

    // Add a small delay before redirecting to allow subscription-details sync to complete
    // This prevents race conditions when navigating from JoinOrSubscribe after purchase
    const redirectTimer = setTimeout(() => {
      // Double-check purchase flag before redirecting (in case it was set during the delay)

      if (isDevEnvironment) {
        return;
      }
      if (
        localStorage.getItem("subscription_purchase_in_progress") === "true"
      ) {
        console.log("⏳ Purchase still in progress - skipping redirect");
        return;
      }

      // If there's no team lead, navigate if user subscription-details is invalid
      if (!leadUid) {
        return;
      }

      // If there's a team lead, wait for both checks to complete
      // Only navigate if both are invalid
      if (!isCheckingUserSubscription && !isCheckingLeadSubscription) {
        const userInvalid = isUserSubscriptionValid === false;
        const leadInvalid = isLeadSubscriptionValid === false;

        console.log("isUserSubscriptionValid " + isUserSubscriptionValid);
        console.log("isLeadSubscriptionValid " + isLeadSubscriptionValid);

        // If team lead subscription-details is valid, don't navigate
        if (isLeadSubscriptionValid === true) {
          return;
        }

        console.log("is dev env renew ->" + isDevEnvironment);
        // Both must be invalid to navigate
        if (userInvalid && leadInvalid) {
          // If user is a team lead, redirect to renew/downgrade page
          // Otherwise, redirect to contact-or-leave page (for team members)
          if (isTeamLead) {
            console.log(
              "❌ TeamsOverview lead subscription-details is INVALID - redirecting to renew-or-downgrade page"
            );
          }
        }
      }
    }, 500); // 500ms delay to allow subscription-details sync to complete

    return () => clearTimeout(redirectTimer);
  }, [
    isLoadingReviewedSubscriptions,
    isLoadingIsTeamLead,
    reviewedSubscriptions,
    isTeamLead,
    isLoadingLeadUid,
    isCheckingSubscription,
    isCheckingUserSubscription,
    isCheckingLeadSubscription,
    isUserSubscriptionValid,
    isLeadSubscriptionValid,
    leadUid,
    navigate,
  ]);

  return {
    isLoading,
    isCheckingSubscription,
    isUserSubscriptionValid,
    isLeadSubscriptionValid,
    leadUid,
    isLoadingLeadUid,
  };
}
